import { useState, useMemo, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Mail,
  BookOpen,
  Shield,
  Users,
  Zap,
  BarChart2,
  Puzzle,
  FlaskConical,
  X,
} from 'lucide-react';
import { useUserStore } from '../store/userStore';
import type { FunctionalRole } from '../types';
import { DEFAULT_FACET_WEIGHTS, DEFAULT_RISK_CONFIG } from '../types';
import { getRiskBandDisplay } from '../lib/riskBand';

// ── Types ────────────────────────────────────────────────────────────────────

interface Article {
  id: string;
  title: string;
  summary: string;
  body: string;
  category: string;
  tags: string[];
  /** Roles most likely to need this article (empty = everyone) */
  audience: Array<FunctionalRole | 'admin'>;
}

// ── Content ───────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all', label: 'All', icon: BookOpen },
  { id: 'getting-started', label: 'Getting Started', icon: Zap },
  { id: 'admin', label: 'Admin & Setup', icon: Shield },
  { id: 'dashboards', label: 'Dashboards', icon: BarChart2 },
  { id: 'risk', label: 'Risk Engine', icon: FlaskConical },
  { id: 'integrations', label: 'Integrations', icon: Puzzle },
  { id: 'team', label: 'Roles & Team', icon: Users },
];

const ARTICLES: Article[] = [
  // Getting Started
  {
    id: 'gs-overview',
    title: 'What is OctoPM?',
    summary: 'An overview of OctoPM and how it keeps every team in sync.',
    body: `OctoPM is an AI-powered product management command center. It ingests signals from your existing tools — Jira, Linear, Slack, and Google Drive — and surfaces a single source of truth for every stakeholder.

**Core concepts:**
- **Projects** represent a unit of work with tickets, risk scores, and stakeholder views.
- **Risk Engine** evaluates project health across 6 facets using AI, producing a score from 1 (safe) to 5 (critical).
- **Dashboards** are role-tailored views. Each functional role (PM, Engineer, Executive, etc.) sees the data most relevant to them.
- **Integrations** sync tickets, comments, and updates automatically so you're always working with live data.`,
    category: 'getting-started',
    tags: ['overview', 'intro', 'concepts'],
    audience: [],
  },
  {
    id: 'gs-first-project',
    title: 'Creating your first project',
    summary: 'How to create a project and add tickets to it.',
    body: `Navigate to the **Portfolio** view and click **New Project**. Fill in:

- **Name** — a clear, descriptive title visible to all stakeholders.
- **Description** — optional but improves AI risk analysis accuracy.
- **Status** — start with *Draft* or *Planning*.

Once created, tickets sync automatically if you've connected Jira or Linear. You can also create tickets manually inside the project.

**Tip:** Connect an integration before creating your first project so tickets import automatically on first sync.`,
    category: 'getting-started',
    tags: ['project', 'create', 'tickets'],
    audience: ['product_manager', 'admin'],
  },
  {
    id: 'gs-dashboards',
    title: 'Finding your dashboard',
    summary: 'How role-based dashboards work and how to switch between them.',
    body: `OctoPM shows a tailored dashboard based on your **functional roles**. If you have multiple functional roles, you can switch between dashboards using the left sidebar.

| Role | Dashboard focus |
|------|-----------------|
| Product Manager | Portfolio health, risk overview, AI briefings, suggested actions |
| Executive | Risk heatmap, delivery confidence, goal progress, portfolio health bar |
| Team Lead | Sprint health, at-risk tickets, velocity, workload balance |
| Engineer | My tickets, context feed |
| Designer | Definition health, scope watch, design-relevant tickets |
| Sales | Delivery confidence, at-risk customer impact, feature status |
| Marketing | Launch readiness, recently completed projects |

If your dashboard doesn't match your role, ask your admin to update your functional roles in **Settings → People & Roles**.`,
    category: 'getting-started',
    tags: ['dashboard', 'roles', 'navigation'],
    audience: [],
  },

  // Admin & Setup
  {
    id: 'admin-invite',
    title: 'Inviting team members',
    summary: 'How admins invite users and assign roles.',
    body: `Go to **Settings → People & Roles** and click **Invite Member**.

Enter the user's email and assign:
- **Entity Role** — *Admin* can manage settings, integrations, and risk config. *Member* is read/write access within their functional roles.
- **Functional Roles** — One or more roles that control which dashboard views the user sees. A user can have multiple functional roles (e.g., both *Product Manager* and *Executive*).

Invites expire after **7 days**. The invited user receives an email with a one-click accept link. They must create a Firebase account if they don't already have one, and must verify their email before access is granted.

**Note:** Inviting users consumes a seat if your plan has a seat limit.`,
    category: 'admin',
    tags: ['invite', 'team', 'roles', 'admin'],
    audience: ['admin'],
  },
  {
    id: 'admin-settings',
    title: 'Company settings',
    summary: 'Configure your company name, logo, timezone, and branding.',
    body: `Go to **Settings → Company** to configure:

- **Company name** — Appears in all dashboard headers.
- **Logo** — Upload a square PNG/JPG (recommended: 512×512px). Stored securely in Firebase Storage, scoped to your entity.
- **Timezone** — Controls how dates and deadlines are displayed across the app.
- **Fiscal year start** — Used for annual planning views (coming soon).

Changes take effect immediately for all users in your organization.`,
    category: 'admin',
    tags: ['company', 'settings', 'logo', 'branding'],
    audience: ['admin'],
  },
  {
    id: 'admin-risk-config',
    title: 'Configuring the Risk Engine',
    summary: 'How admins tune risk weights, thresholds, and ticket scope.',
    body: `Go to **Settings → Risk Configuration** to customize how OctoPM evaluates project health.

**Key settings:**

| Setting | Description | Default |
|---------|-------------|---------|
| Alert threshold | Score ≥ this value triggers email alerts | 3.0 |
| Stale ticket days | Days without a source update before a ticket is "stale" | 5 |
| Min tickets for score | Minimum active tickets before confidence reaches 100% | 3 |
| Included ticket types | Which types count toward risk scoring | All |

**Facet weights** let you emphasize what matters most for your team. Set any facet to 0 to exclude it entirely, or raise a value to amplify its effect on the overall score.

See the **Risk Formula** section for the full calculation.`,
    category: 'admin',
    tags: ['risk', 'config', 'weights', 'threshold', 'admin'],
    audience: ['admin'],
  },

  // Dashboards
  {
    id: 'dash-pm',
    title: 'Product Manager dashboard',
    summary: 'Understanding the Portfolio Health view.',
    body: `The PM dashboard gives you a live read on your entire portfolio.

**Sections:**
- **What's On Deck** — AI-generated briefing highlighting the most urgent actions across all projects. Refreshes daily or on demand.
- **Project Risk Cards** — Each project card shows its overall risk score, status, and key facet breakdown. Click any card to drill into the full project view.
- **Suggested Actions** — Concrete next steps surfaced by the AI based on ticket state, comments, and staleness signals.

**Refreshing insights:** Click the refresh icon next to *What's On Deck* to generate a fresh briefing using the latest data.`,
    category: 'dashboards',
    tags: ['pm', 'product manager', 'portfolio', 'dashboard'],
    audience: ['product_manager'],
  },
  {
    id: 'dash-exec',
    title: 'Executive dashboard',
    summary: 'High-level portfolio health for leadership.',
    body: `The Executive dashboard is designed to give leadership a complete portfolio read in under 2 minutes.

**Sections:**
- **What's On Deck** — AI briefing summarising the highest-priority items across all projects.
- **Portfolio Health Bar** — A single horizontal bar showing the split of projects by risk level (critical, high, medium, healthy) at a glance.
- **Risk Heatmap** — A grid of all projects × all 6 risk facets. Each cell is colour-coded by facet score so you can immediately spot which projects are struggling in which dimensions.
- **Delivery Confidence** — Per-project percentage derived from the overall risk score (an inverted view of the same evaluation — lower risk reads as higher confidence).
- **Goal Progress** — OKR/goal completion status across the portfolio.

Click any project in the heatmap or confidence list to open the full project detail view.`,
    category: 'dashboards',
    tags: ['executive', 'exec', 'portfolio', 'leadership', 'heatmap'],
    audience: ['executive'],
  },
  {
    id: 'dash-teamlead',
    title: 'Team Lead dashboard',
    summary: 'Sprint health, velocity, blockers, and workload balance.',
    body: `The Team Lead dashboard keeps you on top of what your team is working on and where the friction is.

**Sections:**
- **What's On Deck** — AI-generated briefing for your projects, highlighting the most urgent items.
- **Velocity Panel** — Sprint throughput chart for your highest-risk project, showing completed tickets over time.
- **Your Projects** — Risk cards for all your projects. Click any card to drill into the full project view.
- **Needs Attention** — All tickets currently flagged as *At Risk* across your projects in one place.
- **Workload Balance** — Per-assignee breakdown showing % of tickets, % of complexity load (estimate), and % of significance (priority). Use this to spot who is overloaded before it becomes a problem.`,
    category: 'dashboards',
    tags: ['team lead', 'sprint', 'velocity', 'workload', 'blockers'],
    audience: ['team_lead'],
  },
  {
    id: 'dash-engineer',
    title: 'Engineer dashboard',
    summary: 'My tickets and context feed.',
    body: `The Engineer dashboard is focused on your individual work.

**My Tickets** shows all tickets assigned to you across all active projects, sorted by urgency (At Risk first, then On Track, then Planning).

Each ticket shows:
- Status icon (red = at risk, yellow = on track, blue = planning)
- Ticket title
- Parent project name
- Current status label

**Context Feed** shows recent ticket activity across your projects — the three most recently updated open tickets per project, with AI summaries when available. Use this to stay aware of what's changing around your work without needing to open every project.`,
    category: 'dashboards',
    tags: ['engineer', 'tickets', 'my work', 'context feed'],
    audience: ['engineer'],
  },
  {
    id: 'dash-designer',
    title: 'Designer dashboard',
    summary: 'Definition health, scope watch, and design-relevant tickets.',
    body: `The Designer dashboard surfaces the work most relevant to design and definition quality.

**Sections:**
- **Definition Health** — A list of all projects with their definition risk level (derived from the Definition facet score). High definition risk means PRD clarity or acceptance criteria are lacking.
- **Scope Watch** — Scope change tracking (requires integration data from Jira or Linear).
- **Design-Relevant Tickets** — All open *feature* and *enhancement* tickets across your projects in one feed, with type label and status badge. These are the tickets most likely to need design input.
- **My Projects** — Compact project list with status and risk badge. Click any to open the full project view.`,
    category: 'dashboards',
    tags: ['designer', 'design', 'definition', 'scope', 'feature tickets'],
    audience: ['designer'],
  },
  {
    id: 'dash-sales',
    title: 'Sales dashboard',
    summary: 'Delivery confidence, at-risk projects, and feature status.',
    body: `The Sales dashboard gives you the information you need for customer conversations.

**Sections:**
- **Delivery Confidence** — All projects sorted by confidence percentage (lowest first). Derived from the overall risk score. Use this to set accurate expectations with customers.
- **At Risk — Customer Impact** — Projects currently flagged as *At Risk*, with a *Get Update* action for quick follow-up.
- **Feature Status** — A searchable list of all projects with their current status and confidence level. Click any project to see the full detail view.
- **Recently Completed** — Tickets completed in the last 30 days, flagged with **PM ✓** and **MKT ✓** badges when they've been through product and marketing review. These are your "ready to announce" items.`,
    category: 'dashboards',
    tags: ['sales', 'delivery confidence', 'feature status', 'completed'],
    audience: ['sales'],
  },
  {
    id: 'dash-marketing',
    title: 'Marketing dashboard',
    summary: 'Launch readiness and recently completed projects.',
    body: `The Marketing dashboard focuses on what's coming and what's ready to promote.

**Sections:**
- **Launch Readiness** — All active projects (on track, planning, draft) sorted by readiness. Each row shows the project name, current status, and delivery confidence percentage. Use this to plan GTM timing.
- **Recently Completed** — Projects with *complete* status, marked as *Ready to announce*. These are cleared for marketing content, announcements, and release notes.
- **Scope Stability** — Tracks how much project scope is changing over time (requires Jira or Linear integration).
- **GTM Calendar** — Planned release timeline view (coming soon).`,
    category: 'dashboards',
    tags: ['marketing', 'launch', 'gtm', 'completed', 'readiness'],
    audience: ['marketing'],
  },

  // Risk Engine
  {
    id: 'risk-formula',
    title: 'How the risk score is calculated',
    summary: 'The full formula and what each facet means.',
    body: `OctoPM uses a **weighted mean** of 6 risk facets to produce the overall project risk score.

**Formula:**

\`\`\`
S_overall = Σ(S_i × W_i) / Σ(W_i)
\`\`\`

Where each \`S_i\` is a facet score from 1–5 and \`W_i\` is the configured weight for that facet.

**The 6 facets:**

| Facet | What it measures |
|-------|-----------------|
| **Communication** | Team alignment, update frequency, silence detection |
| **Schedule** | Velocity health, milestone tracking, sprint patterns |
| **Definition** | PRD clarity, acceptance criteria completeness |
| **Scope** | Feature creep, requirement volatility |
| **Dependency** | External blockers, cross-team wait states |
| **Deadline** | Delivery confidence, backlog vs velocity |

**Confidence** scales with ticket count:

\`\`\`
Confidence = min(100, max(20, floor(n / n_min × 75) + 25))
\`\`\`

A project with fewer tickets than your configured minimum gets lower confidence — the AI has less signal to work from.

**Trend** compares today's score to the most recent prior evaluation within the last 7 days:
- Worsening: delta > 0.3
- Improving: delta < −0.3
- Stable: otherwise
- Unknown: no prior evaluation to compare against — shown as "No trend yet", never as Stable

**Signals the AI uses:** ticket status, priority, assignee, due dates, stale ticket flags, recent comments from Jira/Linear/Slack, and AI-generated ticket summaries.`,
    category: 'risk',
    tags: ['risk', 'formula', 'score', 'facets', 'calculation'],
    audience: [],
  },
  {
    id: 'risk-refresh',
    title: 'Refreshing risk on demand',
    summary: 'How to trigger a new AI risk evaluation for a project.',
    body: `Inside any project view, click **Refresh Risk** (top right of the project header). This queues a fresh AI risk evaluation using the latest ticket data, comments, and staleness signals.

**Who can refresh:** Admins and Product Managers.

**What happens:**
1. The current ticket snapshot is sent to OctoPM's AI with your entity's risk configuration.
2. The AI scores each of the 6 facets with reasoning and signals.
3. OctoPM computes the weighted overall score and updates the project's risk card.
4. If the new score crosses your alert threshold, stakeholders receive an email notification.

Refresh is fire-and-forget — the page will update within ~30–60 seconds. You can navigate away.`,
    category: 'risk',
    tags: ['risk', 'refresh', 'AI', 'gemini'],
    audience: ['product_manager', 'admin'],
  },
  {
    id: 'risk-alerts',
    title: 'Risk alerts',
    summary: 'When and how alert emails are sent.',
    body: `OctoPM sends an email alert when a project's risk score crosses your configured **alert threshold** (default: 3.0).

**Alert conditions:**
- Score rises from below threshold to at or above it.
- Alert emails are rate-limited: at most once per 6 hours per project.

**Configuring the threshold:** Go to **Settings → Risk Configuration** and adjust the alert threshold slider.

**Who receives alerts:** Currently sent to the project's owner. Full stakeholder notification settings are coming soon.`,
    category: 'risk',
    tags: ['alerts', 'notifications', 'threshold', 'email'],
    audience: ['admin', 'product_manager'],
  },

  // Integrations
  {
    id: 'int-connect',
    title: 'Connecting integrations',
    summary: 'How to connect Jira, Linear, Slack, and Google Drive.',
    body: `Go to **Settings → Integrations** and click **Connect** next to the provider you want to set up.

**Jira Cloud** — OAuth 2.0 flow. You'll be redirected to Atlassian to authorize OctoPM. Select the workspace and projects to sync.

**Jira Data Center (self-hosted)** — Uses email + API token auth (not OAuth). Enter your instance URL (must be HTTPS), your Jira account email, and an API token generated in your Jira account settings. OctoPM validates the credentials before saving.

**Linear** — OAuth 2.0 flow. Authorize and select teams/projects.

**Slack** — OAuth flow. Authorize access to the channels you want OctoPM to monitor for project-related signals.

**Google Drive** — OAuth flow. Grant read access to project documents and PRDs.

After connecting, OctoPM runs an initial sync which may take a few minutes depending on data volume. Subsequent syncs run automatically every hour.

**Admin only:** Only entity admins can connect or disconnect integrations.`,
    category: 'integrations',
    tags: ['jira', 'jira dc', 'data center', 'linear', 'slack', 'google', 'connect', 'sync'],
    audience: ['admin'],
  },
  {
    id: 'int-sync',
    title: 'How sync works',
    summary: 'What data is pulled and how often.',
    body: `OctoPM syncs the following data from your integrations:

**Jira / Linear:**
- Issues/tickets: title, status, type, assignee, priority, due date, labels
- Comments: last ~5 recent comments per ticket (used as AI signals)
- Status transitions: tracked to detect velocity and stagnation

**Slack:**
- Messages in connected channels that mention project names or linked tickets
- Used as communication signals in the risk engine

**Google Drive:**
- Documents linked to projects (title + summary)
- Used as definition quality signals

**Sync frequency:** Automatic sync every 60 minutes. Admins can trigger a manual sync from **Settings → Integrations**.`,
    category: 'integrations',
    tags: ['sync', 'data', 'jira', 'linear', 'slack'],
    audience: [],
  },

  // Roles & Team
  {
    id: 'roles-overview',
    title: 'Roles explained',
    summary: 'System roles, entity roles, and functional roles — what each controls.',
    body: `OctoPM has three layers of role-based access:

**Entity Role** (Admin vs Member)
- *Admin*: can manage settings, integrations, risk config, invite/remove users, and refresh risk.
- *Member*: read/write access within their functional role scope.

**Functional Roles** (what dashboard you see)
Each user can have one or more:
- *Executive* — Portfolio-level KPI dashboard with risk heatmap and goal progress
- *Product Manager* — Full portfolio + risk + AI briefings + suggested actions
- *Team Lead* — Sprint health, velocity, blockers, workload balance
- *Engineer* — My tickets, context feed
- *Designer* — Definition health, scope watch, design-relevant tickets
- *Sales* — Delivery confidence, feature status, recently completed
- *Marketing* — Launch readiness, recently completed projects

**Note:** A user's functional roles are also used by the risk endpoint access control — only *admin* and *product_manager* functional roles can trigger risk refreshes.`,
    category: 'team',
    tags: ['roles', 'admin', 'member', 'functional', 'permissions'],
    audience: ['admin'],
  },
];

// ── Risk Calculator ───────────────────────────────────────────────────────────

const FACET_KEYS = ['communication', 'schedule', 'definition', 'scope', 'dependency', 'deadline'] as const;
type FacetKey = typeof FACET_KEYS[number];

function RiskCalculator() {
  const [scores, setScores] = useState<Record<FacetKey, number>>({
    communication: 2,
    schedule: 2,
    definition: 2,
    scope: 2,
    dependency: 2,
    deadline: 2,
  });
  const [weights, setWeights] = useState<Record<FacetKey, number>>({
    communication: DEFAULT_FACET_WEIGHTS.communication,
    schedule: DEFAULT_FACET_WEIGHTS.schedule,
    definition: DEFAULT_FACET_WEIGHTS.definition,
    scope: DEFAULT_FACET_WEIGHTS.scope,
    dependency: DEFAULT_FACET_WEIGHTS.dependency,
    deadline: DEFAULT_FACET_WEIGHTS.deadline,
  });

  const overall = useMemo(() => {
    let wSum = 0;
    let wTotal = 0;
    for (const key of FACET_KEYS) {
      wSum += scores[key] * weights[key];
      wTotal += weights[key];
    }
    return wTotal === 0 ? 0 : wSum / wTotal;
  }, [scores, weights]);

  // This sandbox exists to teach the formula, so the raw number stays the
  // primary display here (unlike other surfaces, where it is demoted to
  // hover) — but the classification now comes from the shared band utility
  // instead of its own ad hoc cut-points, so it agrees with every other
  // risk surface in the app.
  const { textClass: scoreColor, label: scoreLabel } = getRiskBandDisplay(
    overall,
    DEFAULT_RISK_CONFIG.alertThreshold,
  );

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <FlaskConical className="h-5 w-5 text-primary" />
        <h3 className="text-title text-gray-900 dark:text-white">
          Risk Formula Sandbox
        </h3>
      </div>
      <p className="text-body text-gray-500 dark:text-gray-400 mb-6 max-w-2xl">
        Adjust facet scores (1–5) and weights to see how the overall score changes. This mirrors exactly how the AI evaluation works.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {FACET_KEYS.map((key) => (
          <div key={key} className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-micro font-semibold text-gray-600 dark:text-gray-300 capitalize">
                {key}
              </label>
              <div className="flex items-center gap-3">
                <span className="text-micro text-gray-400 dark:text-gray-500">weight</span>
                <input
                  type="number"
                  min={0}
                  max={5}
                  step={0.5}
                  value={weights[key]}
                  onChange={(e) =>
                    setWeights((w) => ({ ...w, [key]: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-14 text-center text-micro rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white px-2 py-1 focus:outline-none focus:border-primary/50"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={1}
                max={5}
                step={0.5}
                value={scores[key]}
                onChange={(e) =>
                  setScores((s) => ({ ...s, [key]: parseFloat(e.target.value) }))
                }
                className="flex-1 accent-primary"
              />
              <span className="text-meta font-semibold w-8 text-right text-gray-900 dark:text-white tabular-nums">
                {scores[key].toFixed(1)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between px-5 py-4 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06]">
        <div>
          <p className="text-meta text-gray-500 dark:text-gray-400 mb-0.5">Overall Risk Score</p>
          <p className="text-meta text-gray-400 dark:text-gray-500 font-mono">
            Σ(score × weight) / Σ(weight)
          </p>
        </div>
        <div className="text-right">
          <p className={`text-4xl font-bold tabular-nums tracking-tight ${scoreColor}`}>
            {overall.toFixed(2)}
          </p>
          <p className={`text-meta font-semibold mt-0.5 ${scoreColor}`}>{scoreLabel}</p>
        </div>
      </div>
      <p className="text-micro text-gray-400 dark:text-gray-500 mt-3">
        Default alert threshold: {DEFAULT_RISK_CONFIG.alertThreshold}. Scores at or above this level trigger email alerts.
      </p>
    </div>
  );
}

// ── Article modal ─────────────────────────────────────────────────────────────

function ArticleModal({ article, onClose }: { article: Article; onClose: () => void }) {
  // Render simple markdown-ish body (bold, code, tables, newlines)
  const renderBody = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h3 key={i} className="text-title text-gray-900 dark:text-white mt-5 mb-2">{line.slice(3)}</h3>;
      }
      if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
        const content = line.slice(2, -2);
        return <p key={i} className="font-semibold text-gray-800 dark:text-gray-200 mt-3">{content}</p>;
      }
      if (line.startsWith('| ')) {
        return null; // handled below as table
      }
      if (line.startsWith('```')) {
        return null;
      }
      if (line.startsWith('- ') || line.match(/^\d+\./)) {
        const content = line.replace(/^[-\d.]\s*/, '');
        return (
          <li key={i} className="text-body text-gray-600 dark:text-gray-300 ml-4 mb-1 list-disc">
            {renderInline(content)}
          </li>
        );
      }
      if (line.trim() === '') return <div key={i} className="h-2" />;
      return <p key={i} className="text-body text-gray-600 dark:text-gray-300 mb-2">{renderInline(line)}</p>;
    });
  };

  const renderInline = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**'))
        return <strong key={i} className="font-semibold text-gray-800 dark:text-gray-100">{part.slice(2, -2)}</strong>;
      if (part.startsWith('`') && part.endsWith('`'))
        return <code key={i} className="font-mono text-micro bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded text-primary">{part.slice(1, -1)}</code>;
      return part;
    });
  };

  // Extract and render tables
  const renderArticle = (text: string) => {
    const sections: ReactElement[] = [];
    const lines = text.split('\n');
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (line.startsWith('| ')) {
        // Collect table rows
        const tableLines: string[] = [];
        while (i < lines.length && (lines[i].startsWith('| ') || lines[i].startsWith('|--'))) {
          if (!lines[i].startsWith('|--')) tableLines.push(lines[i]);
          i++;
        }
        if (tableLines.length > 0) {
          sections.push(
            <div key={`table-${i}`} className="overflow-x-auto my-3">
              <table className="w-full text-meta border-collapse">
                {tableLines.map((row, ri) => {
                  const cells = row.split('|').filter((c) => c.trim() !== '');
                  return ri === 0 ? (
                    <thead key={ri}>
                      <tr>
                        {cells.map((c, ci) => (
                          <th key={ci} className="text-left px-3 py-2 text-micro font-semibold text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-100 dark:border-white/[0.06]">
                            {c.trim()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  ) : (
                    <tbody key={ri}>
                      <tr className="border-b border-gray-50 dark:border-white/[0.03]">
                        {cells.map((c, ci) => (
                          <td key={ci} className="px-3 py-2 text-gray-600 dark:text-gray-300">
                            {renderInline(c.trim())}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  );
                })}
              </table>
            </div>
          );
        }
      } else if (line.startsWith('```')) {
        // Code block
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        i++;
        sections.push(
          <pre key={`code-${i}`} className="my-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.06] font-mono text-micro text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-pre-wrap">
            {codeLines.join('\n')}
          </pre>
        );
      } else {
        const rendered = renderBody(line);
        if (rendered[0] !== null) sections.push(<div key={i}>{rendered}</div>);
        i++;
      }
    }
    return sections;
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 8 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-white dark:bg-bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-100 dark:border-white/[0.08]"
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-white/[0.06]">
            <div>
              <p className="text-micro font-semibold text-primary uppercase tracking-wider mb-1">
                {CATEGORIES.find((c) => c.id === article.category)?.label}
              </p>
              <h2 className="text-headline text-gray-900 dark:text-white font-display">
                {article.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] text-gray-400 dark:text-gray-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="overflow-y-auto px-6 py-5 flex-1">
            {renderArticle(article.body)}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: 'Why is my risk score 0 or not updating?',
    a: 'Risk scores are generated by AI and require at least one active project with tickets. Make sure your integration is connected and synced. If tickets exist, click "Refresh Risk" inside the project. Scores may take up to 60 seconds to appear.',
  },
  {
    q: 'How do I change what dashboard I see?',
    a: 'Your dashboard is determined by your functional roles. Ask your admin to go to Settings → People & Roles and update your roles. You can also switch between dashboards using the sidebar if you have multiple functional roles.',
  },
  {
    q: 'Why are some fields greyed out in Settings?',
    a: 'Many settings are restricted to Entity Admins. If you need to make changes, ask your admin to either update the setting or upgrade your entity role.',
  },
  {
    q: 'What does "Confidence" mean on a risk score?',
    a: 'Confidence reflects how much data the AI had to work with. Projects with fewer tickets than your configured minimum get lower confidence. It scales from 20% (no data) to 100% (full data). A low-confidence score may not reflect true project health.',
  },
  {
    q: 'How often do integrations sync?',
    a: 'Integrations sync automatically every 60 minutes. Admins can trigger a manual sync from Settings → Integrations. The first sync after connecting may take longer depending on data volume.',
  },
  {
    q: "I can't see the Refresh Risk button. Why?",
    a: 'The Refresh Risk button is only visible to Entity Admins and users with the Product Manager functional role. Ask your admin to update your roles if you need this access.',
  },
  {
    q: 'How do I connect Jira Data Center (self-hosted)?',
    a: 'In Settings → Integrations, choose Jira Data Center. Enter your instance URL (must be HTTPS), your Jira account email, and an API token from your Jira account settings. OctoPM validates the credentials before saving — no OAuth redirect required.',
  },
  {
    q: 'What is the Workload Balance panel on the Team Lead dashboard?',
    a: 'Workload Balance shows each assignee\'s share of tickets, complexity load (based on ticket estimates), and significance (based on priority). The three stacked bars let you spot who is carrying a disproportionate load before it becomes a problem.',
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-4">
      {FAQS.map((faq, i) => (
        <div key={i} className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
            onClick={() => setOpen(open === i ? null : i)}
          >
            <span className="text-meta font-medium text-gray-900 dark:text-white">{faq.q}</span>
            {open === i
              ? <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              : <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />}
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
              >
                <p className="px-5 pb-4 text-meta text-gray-500 dark:text-gray-400">{faq.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function Support() {
  const { sessionConfig } = useUserStore();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState<Article | null>(null);

  const isAdmin = sessionConfig?.entityRole === 'admin';
  const functionalRoles: FunctionalRole[] = sessionConfig?.functionalRoles ?? [];

  const filteredArticles = useMemo(() => {
    return ARTICLES.filter((a) => {
      if (category !== 'all' && a.category !== category) return false;
      if (query) {
        const q = query.toLowerCase();
        if (
          !a.title.toLowerCase().includes(q) &&
          !a.summary.toLowerCase().includes(q) &&
          !a.tags.some((t) => t.includes(q)) &&
          !a.body.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    }).sort((a, b) => {
      // Prioritize articles relevant to the user's roles
      const aRelevant =
        a.audience.length === 0 ||
        a.audience.some(
          (r) => r === 'admin' ? isAdmin : functionalRoles.includes(r as FunctionalRole)
        );
      const bRelevant =
        b.audience.length === 0 ||
        b.audience.some(
          (r) => r === 'admin' ? isAdmin : functionalRoles.includes(r as FunctionalRole)
        );
      if (aRelevant && !bRelevant) return -1;
      if (!aRelevant && bRelevant) return 1;
      return 0;
    });
  }, [query, category, isAdmin, functionalRoles]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-bg-deep">
      {/* Hero */}
      <div className="relative overflow-hidden bg-primary px-6 py-16 text-center">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-white/[0.08] blur-3xl" />
        </div>
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-white/20 backdrop-blur-sm mb-5 mx-auto">
              <BookOpen className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-headline text-white font-display mb-3">
              How can we help?
            </h1>
            <p className="text-white/70 text-body mb-8 max-w-md mx-auto">
              Guides, tutorials, and answers for every role.
            </p>

            {/* Search */}
            <div className="max-w-lg mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setCategory('all'); }}
                placeholder="Search articles, topics, roles…"
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white dark:bg-bg-surface text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 text-meta shadow-xl border border-white/20 dark:border-white/[0.08] focus:outline-none focus:ring-2 focus:ring-white/40 dark:focus:ring-white/20"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Category tabs */}
        {!query && (
          <div className="flex items-center gap-2 flex-wrap mb-8">
            {CATEGORIES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setCategory(id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-meta font-medium transition-colors ${
                  category === id
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-white dark:bg-bg-surface text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-white/[0.06] hover:border-primary/40 dark:hover:border-primary/40 hover:text-primary dark:hover:text-primary'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Articles */}
          <div className="lg:col-span-2 space-y-8">
            {/* Search results / article grid */}
            <div>
              {query && (
                <p className="text-meta text-gray-500 dark:text-gray-400 mb-4">
                  {filteredArticles.length} result{filteredArticles.length !== 1 ? 's' : ''} for "{query}"
                </p>
              )}
              {filteredArticles.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="h-8 w-8 text-gray-300 dark:text-gray-500 mx-auto mb-3" />
                  <p className="text-body font-medium text-gray-500 dark:text-gray-400">No articles match "{query}"</p>
                  <button
                    onClick={() => setQuery('')}
                    className="mt-3 text-meta text-primary hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredArticles.map((article) => {
                    const cat = CATEGORIES.find((c) => c.id === article.category);
                    const CatIcon = cat?.icon ?? BookOpen;
                    const isRelevant =
                      article.audience.length === 0 ||
                      article.audience.some((r) =>
                        r === 'admin' ? isAdmin : functionalRoles.includes(r as FunctionalRole)
                      );
                    return (
                      <motion.button
                        key={article.id}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => setSelected(article)}
                        className="w-full text-left bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 hover:shadow-sm transition-shadow group"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 p-2 rounded-lg ${isRelevant ? 'bg-primary/10' : 'bg-gray-100 dark:bg-white/5'}`}>
                            <CatIcon className={`h-4 w-4 ${isRelevant ? 'text-primary' : 'text-gray-400 dark:text-gray-500'}`} />
                          </div>
                          <div>
                            <p className="text-title text-gray-900 dark:text-white group-hover:text-primary transition-colors leading-snug">
                              {article.title}
                            </p>
                            <p className="text-meta text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                              {article.summary}
                            </p>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Risk sandbox — show in risk category or when not searching */}
            {(category === 'risk' || category === 'all') && !query && (
              <RiskCalculator />
            )}

            {/* FAQ */}
            {(category === 'all' || category === 'getting-started') && !query && (
              <div>
                <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
                  Frequently Asked Questions
                </h2>
                <FAQ />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Contact */}
            <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-5 w-5 text-primary" />
                <h3 className="text-title text-gray-900 dark:text-white">
                  Still need help?
                </h3>
              </div>
              <p className="text-meta text-gray-500 dark:text-gray-400 mb-4">
                Our team typically replies within one business day.
              </p>
              <a
                href="mailto:support@octo.pm"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-primary text-white text-meta font-medium hover:bg-primary/90 transition-colors"
              >
                <Mail className="h-4 w-4" />
                Email support@octo.pm
              </a>
            </div>

            {/* Role-specific highlights */}
            {functionalRoles.length > 0 && (
              <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-2xl p-5">
                <h3 className="text-title text-gray-900 dark:text-white mb-6">
                  Relevant for your roles
                </h3>
                <div className="space-y-4">
                  {ARTICLES.filter(
                    (a) =>
                      a.audience.length > 0 &&
                      a.audience.some((r) =>
                        r === 'admin' ? isAdmin : functionalRoles.includes(r as FunctionalRole)
                      )
                  )
                    .slice(0, 5)
                    .map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setSelected(a)}
                        className="w-full text-left text-micro text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors py-1"
                      >
                        → {a.title}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Quick nav */}
            <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-2xl p-5">
              <h3 className="text-title text-gray-900 dark:text-white mb-6">
                Browse by topic
              </h3>
              <div className="space-y-1.5">
                {CATEGORIES.filter((c) => c.id !== 'all').map(({ id, label, icon: Icon }) => {
                  const count = ARTICLES.filter((a) => a.category === id).length;
                  return (
                    <button
                      key={id}
                      onClick={() => { setCategory(id); setQuery(''); }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-meta transition-colors ${
                        category === id
                          ? 'bg-primary/10 text-primary'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.03]'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {label}
                      </span>
                      <span className="text-micro text-gray-400 dark:text-gray-500">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article modal */}
      {selected && <ArticleModal article={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
